<template>
  <div class="ww-admin-main">
    <div class="container">
      <div class="box">
        <h1 class="title">Club Admin Panel</h1>
        <div class="tabs">
          <ul>
            <li
              :class="{ 'is-active': currentTab == 'club' }"
              @click="currentTab = 'club'"
            >
              <a>
                <span>Club Subscriptions</span>
              </a>
            </li>
            <li
              :class="{ 'is-active': currentTab == 'settings' }"
              @click="currentTab = 'settings'"
            >
              <a>
                <span>Settings</span>
              </a>
            </li>
          </ul>
        </div>
        <div v-if="currentTab == 'club'" class="columns">
          <div class="column">
            <h2 class="subtitle">Club</h2>
            <div class="buttons mb-0">
              <button
                v-for="(plan, index) in plans"
                :key="index"
                @click="selectedPlanIndex = index"
                class="button"
                :class="{
                  button: true,
                  'is-warning': selectedPlanIndex === index,
                }"
              >
                {{ plan.name }}
              </button>
            </div>
            <div class="mb-4">
              <p v-if="plans[selectedPlanIndex].description">
                <small>{{ plans[selectedPlanIndex].description }}</small>
              </p>
            </div>
            <h2 class="subtitle">Member Filter</h2>
            <progress
              v-if="!subscriptionsLoaded"
              class="progress is-warning"
              max="100"
            ></progress>
            <div v-if="subscriptionsLoaded" class="buttons">
              <button
                :class="{ button: true, 'is-warning': updateAll }"
                @click="updateAll = true"
              >
                All {{ rootSubscriptionsByPlanName.length }} Members
              </button>
              <div class="columns">
                <div class="column">
                  <button
                    :class="{ button: true, 'is-warning': !updateAll }"
                    @click="updateAll = false"
                  >
                    Members Since
                  </button>
                </div>
                <div class="column">
                  <input
                    v-if="!updateAll"
                    class="input"
                    type="date"
                    v-model="updateOnlySubscriptionsCreatedAfter"
                  />
                </div>
              </div>
            </div>
            <div v-if="subscriptionsLoaded && subscriptionsToProcess.length > 0" class="card block">
              <header @click="showMembers = !showMembers" class="card-header">
                <p class="card-header-title">
                  View {{ subscriptionsToProcess.length }} Included Members
                </p>
                <button class="card-header-icon" aria-label="more options">
                  <span class="icon">
                    <svg
                      width="24"
                      height="24"
                      version="1.1"
                      viewBox="0 0 752 752"
                      xmlns="http://www.w3.org/2000/svg"
                      :class="{ flipped: showMembers }"
                    >
                      <defs>
                        <clipPath id="a">
                          <path d="m139.21 238h473.58v276h-473.58z" />
                        </clipPath>
                      </defs>
                      <g clip-path="url(#a)">
                        <path
                          d="m373.3 513.5 232.15-232.15c9.8047-9.7969 9.8047-25.688 0-35.488-9.7852-9.8047-25.684-9.8047-35.488 0l-196.66 196.67-191.24-191.26c-9.8047-9.8047-25.699-9.8047-35.488 0-9.8047 9.7969-9.8047 25.688-0.007812 35.488z"
                        />
                      </g>
                    </svg>
                  </span>
                </button>
              </header>
              <div v-if="showMembers" class="card-content">
                <vue-good-table
                  :columns="memberColumns"
                  :rows="selectedMembers"
                  :search-options="{
                    enabled: true,
                    placeholder: 'Search Members',
                  }"
                  :sort-options="{ enabled: true }"
                  :pagination-options="{ enabled: true, perPage: 5 }"
                  styleClass="vgt-table bordered condensed striped"
                />
              </div>
            </div>
            <h2 class="subtitle">Next charge date</h2>
            <input class="input" type="date" v-model="nextChargeDate" />
          </div>
          <div class="column">
            <div>
              <products
                :plan="plans[selectedPlanIndex]"
                :allProducts="allProducts"
                :allSubscriptions="allSubscriptions"
                :nextChargeDate="nextChargeDate"
                @commit="runBatch"
              />            
            </div>
                <div :class="{ modal: true, 'is-active': state == 1 }">
                <div class="modal-background"></div>
                <div class="modal-content">
                  <article class="message is-info">
                    <div class="message-header">
                      <p>Processing</p>                      
                    </div>
                    <div class="message-body">
                      Processed {{ processed }} of {{ subscriptionsToProcess.length }} subscriptions
                      <progress class="progress is-warning" :value="processed" :max="subscriptionsToProcess.length"></progress>
                    </div>
                  </article>
                </div>                
              </div>
              <div :class="{ modal: true, 'is-active': state == 2 }">
                <div class="modal-background"></div>
                <div class="modal-content">
                  <article class="message is-success">
                    <div class="message-header">
                      <p>Complete</p>
                      <button
                        class="delete"
                        @click="reset"
                        aria-label="delete"
                      ></button>
                    </div>
                    <div class="message-body">
                      All {{ subscriptionsToProcess.length }} subscriptions have been processed
                    </div>
                  </article>
                </div>
                <button
                  class="modal-close is-large"
                  @click="reset"
                  aria-label="close"
                ></button>
              </div>
          </div>
        </div>
        <div v-if="currentTab == 'settings'">
          <div v-if="!settingsLoaded">
            <progress class="progress is-warning" max="100"></progress>
          </div>
          <div v-if="settingsLoaded" class="disableActions">
            <h2 class="subtitle">Buy Now Button</h2>
            <div class="buttons">
              <button
                :class="{ button: true, 'is-warning': !disableBuyNow }"
                @click="updateBuyNow(false)"
              >
                Enabled
              </button>
              <button
                :class="{ button: true, 'is-warning': disableBuyNow }"
                @click="updateBuyNow(true)"
              >
                Disabled
              </button>
            </div>
            <h2 class="subtitle">Skip Shipment Button</h2>
            <div class="buttons">
              <button
                :class="{ button: true, 'is-warning': !disableSkip }"
                @click="updateSkip(false)"
              >
                Enabled
              </button>
              <button
                :class="{ button: true, 'is-warning': disableSkip }"
                @click="updateSkip(true)"
              >
                Disabled
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { find } from "lodash";
import products from "./Products.vue";
import { VueGoodTable } from "vue-good-table";

const rootMarker = "Club";

export default {
  name: "admin",

  components: {
    products,
    VueGoodTable,
  },

  data: () => ({
    state: 0,
    processed: 0,
    subscriptionsCount: 0,
    perPage: 50,
    currentPage: 1,
    selectedPlanIndex: 0,
    allSubscriptions: [],
    allCustomers: {},
    customerCount: 0,
    plans: [
      {
        name: "Test Batch",
        description:
          "Testing: Only for accounts with responsival or westwardwhiskey in email.",
        qty: 3,
      },
      {
        name: "Expedition",
        qty: 1,
      },
      {
        name: "Founder's",
        qty: 3,
      },
    ],

    updateAll: true,
    updateOnlySubscriptionsCreatedAfter: null,
    disableBuyNow: null,
    disableSkip: null,
    settingsLoaded: false,
    subscriptionsLoaded: false,
    showMembers: false,
    nextChargeDate: null,
    currentTab: "club",
    memberColumns: [
      { label: "Name", field: "name" },
      { label: "Email", field: "email" },
      {
        label: "Subscribed On",
        field: "createdAt",
        type: "date",
        dateOutputFormat: "MM/dd/yyyy",
        dateInputFormat: "yyyy-MM-dd'T'HH:mm:ss",
      },
    ],
  }),
  props: ["allProducts"],

  computed: {
    subscriptionsToProcess() {
      if (!this.allSubscriptions) {
        return [];
      }

      return (
        this.updateAll
          ? this.rootSubscriptionsByPlanName
          : this.subscriptionsCreatedAfter
      ).map((sub) => sub.address_id);
    },
    rootSubscriptionsByPlanName() {
      // Ex. All subscriptions which title contains 'Explorer Club Auto renew'
      if (!this.allSubscriptions) {
        return;
      }

      if (this.plans[this.selectedPlanIndex].name !== "Test Batch") {
        return this.allSubscriptions.filter((sub) =>
          sub.product_title.includes(
            `${this.plans[this.selectedPlanIndex].name} ${rootMarker}`
          )
        );
      } else {
        return this.allSubscriptions.filter(
          (sub) =>
            sub.email.includes("responsival.com") 
            || sub.email.includes("test@westwardwhiskey.com")
        );
      }
    },
    subscriptionsCreatedAfter() {
      // Ex. All subscriptions which title contains 'Explorer Club Auto renew'
      return this.rootSubscriptionsByPlanName.filter(
        (sub) => sub.created_at >= this.updateOnlySubscriptionsCreatedAfter
      );
    },
    selectedMembers() {
      if (!this.allSubscriptions) {
        return [];
      }

      return (
        this.updateAll
          ? this.rootSubscriptionsByPlanName
          : this.subscriptionsCreatedAfter
      ).map((sub) => {
        let customer = this.allCustomers[sub.customer_id];
        let name = '';
        if (customer)
        {
          name = `${customer.first_name} ${customer.last_name}`;
        }
        return { name: name, email: sub.email, createdAt: sub.created_at };
      });
    },
    totalPages() {
      return Math.ceil(this.subscriptionsCount / this.perPage);
    },
    totalCustomerPages() {
      return Math.ceil(this.customerCount / this.perPage);
    },
  },

  async created() {
    this.subscriptionsCount = await this.getCount();
    this.allSubscriptions = await this.fetchAllSubscriptions();    
    let metafields = await this.getMetafields();
    let hideBuyNow = find(metafields, (m) => m.key == "hide-buy-now");
    let hideSkip = find(metafields, (m) => m.key == "hide-skip");
    this.disableBuyNow = hideBuyNow && hideBuyNow.value == "1" ? true : false;
    this.disableSkip = hideSkip && hideSkip.value == "1" ? true : false;
    this.settingsLoaded = true;
    this.customerCount = await this.getCustomerCount();
    this.allCustomers = await this.fetchAllCustomers();
    this.subscriptionsLoaded = true;
    this.reset();
  },

  methods: {
    async fetchAllSubscriptions() {
      try {
        const subscriptionPromises = [];
        for (let page = 0; page < this.totalPages; page++) {
          const delay = 500 * page;
          subscriptionPromises.push(
            // eslint-disable-next-line no-async-promise-executor
            new Promise(async function(resolve) {
              await new Promise(res => setTimeout(res, delay));
              let result = axios.get(`/subscriptions?page=${page + 1}`);
              resolve(result);
            })          
          );
        }

        return Promise.all(subscriptionPromises).then((values) => {
          let all = [];

          values.forEach((v) => {
            v.data.forEach((entry) => {
              all.push(entry);
            });
          });

          return all;
        }); // return _get(subscriptions, 'data');
      } catch (err) {
        console.warn("Error: Unable to retrieve initial data");
      }
    },

    async fetchAllCustomers() {
      try {
        const customerPromises = [];
        for (let page = 0; page < this.totalCustomerPages; page++) {
          const delay = 500 * page;
          customerPromises.push(
            // eslint-disable-next-line no-async-promise-executor
            new Promise(async function(resolve) {
              await new Promise(res => setTimeout(res, delay));
              let result = axios.get(`/customers?page=${page + 1}`);
              resolve(result);
            })                
          );
        }

        return Promise.all(customerPromises).then((values) => {
          let all = {};

          values.forEach((v) => {
            v.data.forEach((entry) => {
              all[entry.id] = entry;
            });
          });

          return all;
        }); // return _get(subscriptions, 'data');
      } catch (err) {
        console.warn("Error: Unable to retrieve initial data");
      }
    },

    async getCount() {
      try {
        const res = await axios.get(`/count`);
        return res.data.count;
      } catch (err) {
        console.warn("Error: Unable to retrieve initial data");
      }
    },

    async getCustomerCount() {
      try {
        const res = await axios.get(`/customer-count`);
        return res.data.count;
      } catch (err) {
        console.warn("Error: Unable to retrieve initial data");
      }
    },

    async getMetafields() {
      try {
        const res = await axios.get(`/metafields`);
        return res.data;
      } catch (err) {
        console.warn("Error: Unable to retrieve metafield data");
      }
    },

    async updateSkip(disableSkip) {
      try {
        this.disableSkip = disableSkip;
        const res = await axios.post(`/metafields`, {
          key: "hide-skip",
          value: this.disableSkip ? "1" : "0",
          value_type: "integer",
        });
        return res.data;
      } catch (err) {
        console.warn("Error: Unable to save skip flag");
      }
    },

    async updateBuyNow(disableBuyNow) {
      try {
        this.disableBuyNow = disableBuyNow;
        const res = await axios.post(`/metafields`, {
          key: "hide-buy-now",
          value: this.disableBuyNow ? "1" : "0",
          value_type: "integer",
        });
        return res.data;
      } catch (err) {
        console.warn("Error: Unable to save buy now flag");
      }
    },

    async runBatch(selectedProducts, nextChargeDate) {
      console.log("selectedProducts", selectedProducts);
      console.log("addresses", this.subscriptionsToProcess);

      this.state = 1;
      this.processed = 0;
      try {
        for (const address_id of this.subscriptionsToProcess) {
          await axios
            .post("/update-subscriptions", {
              planName: this.plans[this.selectedPlanIndex].name,
              address_id,
              selectedProducts,
              nextChargeDate,
            })
            .catch(function (error) {
              console.groupCollapsed("runBatch error");
              console.log("address id", address_id);
              console.log("axios error", error);
              console.groupEnd();
            });
          this.processed += 1;
        }
        this.state = 2;
      } catch (err) {
        alert("Error: Update failed.");
      }
      // this.reset();
    },
    reset() {
      this.state = 0;
      /* this.allProducts.map(p => {
        p.selected = false;
        return p;
      }) */
    },
  },
};
</script>

<style>
@import "../../assets/bulma.short.css";
@import "../../../node_modules/vue-good-table/dist/vue-good-table.css";

.ww-admin-main {
  font-size: 14px;
  padding: 2em;
}

.button,
.input,
.label,
.pagination {
  font-size: 1em;
}

.card {
  border: 2px solid transparent;
}

.product-selected {
  border: 2px solid #ffe08a;
}

.flipped {
  transform: rotate(180deg);
}

table.vgt-table,
.vgt-wrap__footer,
.vgt-wrap__footer .footer__row-count__label,
.vgt-wrap__footer .footer__row-count__label,
.vgt-wrap__footer .footer__navigation__page-btn span,
.vgt-wrap__footer .footer__row-count__select,
.vgt-wrap__footer .footer__navigation,
.vgt-input,
.vgt-select {
  font-size: 12px;
}

.vgt-wrap__footer .footer__row-count__label {
  padding-top: 2px;
}
</style>
<style scoped>
.admin {
  display: flex;
  flex-flow: row;
}
.menu {
  width: 20%;
  display: flex;
  flex-flow: column;
  border-right: 1px solid gray;
  padding: 0 1.5rem 0 0;
  margin: 0 1.5rem 0 0;
}
.filterSettings {
  margin-bottom: 1em;
}
.filterSettings [type="checkbox"] {
  margin-left: 0;
}
.filterSettings small {
  margin-top: -0.3em;
}
.filterSettings #createdAfterDate {
  width: 100%;
}
a {
  padding: 1rem;
  color: #2c3e50;
  text-decoration: none;
}
.plan {
  margin-top: 2rem;
}
.active {
  border: 2px solid gray;
}

.success {
  margin-top: 2rem;
  color: green;
}
</style>

